import { generateTabLabel } from "@/components/SearchResultGroup/utils/generateTabLabel";
import { TabProps } from "@/components/Tab";
import useFetch from "@/hooks/useFetch";
import client from "@/utils/api/client";
import { SearchIndexResponse } from "@/web-client/api";
import { SearchResultTabType } from "@/consts/tabType";
import { useMemo } from "react";

const useTabs = (tabType: SearchResultTabType, keyword: string): TabProps[] => {
  const { data, isLoading, error } = useFetch<SearchIndexResponse>(
    `/search/count?keyword=${keyword}`,
    async () => {
      const { data } = await client.searchGet({
        keyword: keyword as string,
        limit: 0,
      });

      return data;
    },
    null,
    { waitForAuth: false },
    { suspense: false },
  );

  const isComplete = useMemo<boolean>(() => {
    if (error) return false;

    return data && !isLoading;
  }, [isLoading, data, error]);

  const tabs = useMemo(() => {
    return [
      {
        isCurrent: tabType === SearchResultTabType.All,
        to: {
          pathname: "/search",
          query: { keyword },
        },
        label: generateTabLabel(
          "すべて",
          data
            ? data.makers_total_count +
                data.lists_total_count +
                data.boards_total_count +
                data.building_materials_total_count +
                data.interiors_total_count +
                data.projects_total_count +
                data.posts_total_count +
                data.teams_total_count
            : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Team,
        to: {
          pathname: "/search/teams",
          query: { keyword },
        },
        label: generateTabLabel(
          "チーム",
          data ? data.teams_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Maker,
        to: {
          pathname: "/search/makers",
          query: { keyword },
        },
        label: generateTabLabel(
          "メーカー",
          data ? data.makers_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.List,
        to: {
          pathname: "/search/lists",
          query: { keyword },
        },
        label: generateTabLabel(
          "リスト",
          data ? data.lists_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Board,
        to: {
          pathname: "/search/boards",
          query: { keyword },
        },
        label: generateTabLabel(
          "マテリアルボード",
          data ? data.boards_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.BuilidngMaterial,
        to: {
          pathname: "/search/building_materials",
          query: { keyword },
        },
        label: generateTabLabel(
          "建材",
          data ? data.building_materials_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Interior,
        to: {
          pathname: "/search/interiors",
          query: { keyword },
        },
        label: generateTabLabel(
          "家具",
          data ? data.interiors_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Project,
        to: {
          pathname: "/search/projects",
          query: { keyword },
        },
        label: generateTabLabel(
          "プロジェクト",
          data ? data.projects_total_count : 0,
          isComplete,
        ),
      },
      {
        isCurrent: tabType === SearchResultTabType.Post,
        to: {
          pathname: "/search/posts",
          query: { keyword },
        },
        label: generateTabLabel(
          "写真",
          data ? data.posts_total_count : 0,
          isComplete,
        ),
      },
    ];
  }, [data, keyword, tabType, isComplete]);

  return tabs;
};

export default useTabs;
